/**
 * TextureEditor.js
 * Einzelne Texturen innerhalb eines Texture Packs bearbeiten.
 * - 3D Block-Vorschau mit direktem Malen (Texture3DPainter)
 * - 2D Pixel-Canvas fuer praezises Bearbeiten
 * - Vanilla-Texturen als Vorlage
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, getAuthToken } from '../services/api';
import { useToastContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import PixelCanvas from '../components/pixel-editor/PixelCanvas';
import ToolBar from '../components/pixel-editor/ToolBar';
import ColorPicker from '../components/pixel-editor/ColorPicker';
import { usePixelHistory } from '../components/pixel-editor/usePixelHistory';
import Texture3DPainter from '../components/3d-editor/Texture3DPainter';
import { getBlockGroup, resolveToSixFaces, getUniqueTextureSlots, isGroupMember } from '../data/blockFaceGroups';

// Textur-Kategorien fuer die Navigation
const TEXTURE_CATEGORIES = [
  { id: 'block',    label: 'Bloecke',   icon: '🧱', path: 'block/' },
  { id: 'item',     label: 'Items',     icon: '⚔️',  path: 'item/' },
  { id: 'entity',   label: 'Entities',  icon: '🐄',  path: 'entity/' },
  { id: 'gui',      label: 'GUI',       icon: '📦',  path: 'gui/' },
  { id: 'misc',     label: 'Sonstige',  icon: '🎨',  path: '' },
];

// Bekannte Pflanzen/Kreuzform-Bloecke in Minecraft (Dateiname ohne .png)
// Exakte Dateinamen die als Kreuzform (X-Shape) dargestellt werden
const CROSS_SHAPE_BLOCKS = new Set([
  // Blumen
  'dandelion', 'poppy', 'blue_orchid', 'allium', 'azure_bluet',
  'red_tulip', 'orange_tulip', 'white_tulip', 'pink_tulip',
  'oxeye_daisy', 'cornflower', 'lily_of_the_valley', 'wither_rose',
  'torchflower', 'pink_petals',
  // Setzlinge
  'oak_sapling', 'spruce_sapling', 'birch_sapling', 'jungle_sapling',
  'acacia_sapling', 'dark_oak_sapling', 'cherry_sapling', 'mangrove_propagule',
  'bamboo_sapling',
  // Gras/Farn
  'short_grass', 'tall_grass_bottom', 'tall_grass_top', 'grass',
  'fern', 'large_fern_bottom', 'large_fern_top',
  'dead_bush',
  // Pilze
  'brown_mushroom', 'red_mushroom', 'crimson_fungus', 'warped_fungus',
  // Pflanzen
  'sugar_cane', 'bamboo_stalk', 'kelp', 'kelp_plant', 'seagrass',
  'tall_seagrass_bottom', 'tall_seagrass_top',
  'sweet_berry_bush_stage0', 'sweet_berry_bush_stage1',
  'sweet_berry_bush_stage2', 'sweet_berry_bush_stage3',
  'cave_vines', 'cave_vines_lit', 'cave_vines_plant', 'cave_vines_plant_lit',
  'spore_blossom', 'hanging_roots', 'nether_sprouts',
  'warped_roots', 'crimson_roots', 'twisting_vines', 'twisting_vines_plant',
  'weeping_vines', 'weeping_vines_plant',
  // Dripleaf, Pitcher, Azalea
  'big_dripleaf_stem', 'big_dripleaf_tip', 'big_dripleaf_side',
  'small_dripleaf_side', 'small_dripleaf_stem_bottom', 'small_dripleaf_stem_top',
  'pitcher_crop_bottom', 'pitcher_crop_top', 'pitcher_crop_side',
  'pitcher_plant_bottom', 'pitcher_plant_top',
  'azalea_plant', 'flowering_azalea',
  // Amethyst
  'amethyst_cluster', 'large_amethyst_bud', 'medium_amethyst_bud', 'small_amethyst_bud',
  'pointed_dripstone_down_base', 'pointed_dripstone_down_frustum',
  'pointed_dripstone_down_middle', 'pointed_dripstone_down_tip',
  'pointed_dripstone_down_tip_merge',
  'pointed_dripstone_up_base', 'pointed_dripstone_up_frustum',
  'pointed_dripstone_up_middle', 'pointed_dripstone_up_tip',
  'pointed_dripstone_up_tip_merge',
  // Fackeln
  'torch', 'soul_torch', 'redstone_torch', 'redstone_torch_off',
  // Nutzpflanzen (Stages)
  'wheat_stage0', 'wheat_stage1', 'wheat_stage2', 'wheat_stage3',
  'wheat_stage4', 'wheat_stage5', 'wheat_stage6', 'wheat_stage7',
  'carrots_stage0', 'carrots_stage1', 'carrots_stage2', 'carrots_stage3',
  'potatoes_stage0', 'potatoes_stage1', 'potatoes_stage2', 'potatoes_stage3',
  'beetroots_stage0', 'beetroots_stage1', 'beetroots_stage2', 'beetroots_stage3',
  'nether_wart_stage0', 'nether_wart_stage1', 'nether_wart_stage2',
  'melon_stem', 'pumpkin_stem', 'attached_melon_stem', 'attached_pumpkin_stem',
  // Korallen
  'fire_coral', 'horn_coral', 'tube_coral', 'brain_coral', 'bubble_coral',
  'dead_fire_coral', 'dead_horn_coral', 'dead_tube_coral',
  'dead_brain_coral', 'dead_bubble_coral',
  'fire_coral_fan', 'horn_coral_fan', 'tube_coral_fan',
  'brain_coral_fan', 'bubble_coral_fan',
  // Seerosen etc.
]);

// Flat/Item-Darstellung: Bloecke die als flaches Sprite gezeigt werden
// (nicht X-kreuz, nicht Wuerfel, sondern einfach flach)
const FLAT_SHAPE_BLOCKS = new Set([
  'lever', 'chain', 'end_rod', 'lightning_rod',
  'flower_pot', 'brewing_stand', 'brewing_stand_base',
  'glow_lichen', 'sculk_vein',
  'tripwire', 'tripwire_hook', 'string',
  // Flache Pflanzenteile (Aufsicht)
  'big_dripleaf_top', 'lily_pad',
  // Rails
  'rail', 'powered_rail', 'powered_rail_on', 'detector_rail', 'detector_rail_on',
  'activator_rail', 'activator_rail_on', 'rail_corner',
  // Redstone
  'redstone_dust_dot', 'redstone_dust_line0', 'redstone_dust_line1',
  'redstone_dust_overlay',
  'comparator', 'comparator_on', 'repeater', 'repeater_on',
  // Kerzen
  'candle', 'candle_lit',
  'white_candle', 'white_candle_lit', 'orange_candle', 'orange_candle_lit',
  'magenta_candle', 'magenta_candle_lit', 'light_blue_candle', 'light_blue_candle_lit',
  'yellow_candle', 'yellow_candle_lit', 'lime_candle', 'lime_candle_lit',
  'pink_candle', 'pink_candle_lit', 'gray_candle', 'gray_candle_lit',
  'light_gray_candle', 'light_gray_candle_lit', 'cyan_candle', 'cyan_candle_lit',
  'purple_candle', 'purple_candle_lit', 'blue_candle', 'blue_candle_lit',
  'brown_candle', 'brown_candle_lit', 'green_candle', 'green_candle_lit',
  'red_candle', 'red_candle_lit', 'black_candle', 'black_candle_lit',
  // Knöpfe, Druckplatten (zu flach fuer Block)
  'lantern', 'soul_lantern',
]);

// Muster die eindeutig auf Nicht-Block-Darstellungen hinweisen
const CROSS_PATTERNS = [
  /_(sapling|fungus|roots|roots_pot|sprouts)$/,
  /_bush_stage\d+$/,
  /^(tall_|large_)?(grass|fern|seagrass)_(bottom|top)$/,
  /^(twisting|weeping|cave)_vines/,
  /^(hanging|pitcher)/,
  /dripleaf/,  // big_dripleaf_*, small_dripleaf_*
];
const FLAT_PATTERNS = [
  /_(rail|rail_on|rail_corner)$/,
  /^(rail|powered_rail|detector_rail|activator_rail)/,
  /_candle(_lit)?$/,
  /^candle(_lit)?$/,
  /_dust_/,
  /^(comparator|repeater)(_on)?$/,
  // Tueren, Falltüren, Luken (flache Texturen)
  /_door_(bottom|top)$/,
  /_trapdoor$/,
];

/**
 * Bestimmt den 3D-Geometrie-Typ basierend auf Kategorie und Dateiname.
 *   - item/  → 'item' (flaches Sprite)
 *   - block/ + Pflanze → 'cross' (Kreuzform)
 *   - block/ + flaches Objekt → 'item' (flaches Sprite)
 *   - block/ (alles andere) → 'block' (Wuerfel)
 *   - entity/gui/misc → null (kein 3D, nur 2D)
 */
function getGeometryType(assetPath) {
  if (assetPath.startsWith('item/')) return 'item';
  if (assetPath.startsWith('block/')) {
    const fileName = assetPath.split('/').pop().replace('.png', '');
    if (CROSS_SHAPE_BLOCKS.has(fileName)) return 'cross';
    if (FLAT_SHAPE_BLOCKS.has(fileName)) return 'item';
    // Pattern-Match fuer unbekannte Namen
    if (CROSS_PATTERNS.some(p => p.test(fileName))) return 'cross';
    if (FLAT_PATTERNS.some(p => p.test(fileName))) return 'item';
    return 'block';
  }
  // Entity, GUI, Misc → kein spezieller 3D-Typ
  return null;
}

function TextureEditor() {
  const { id: packId } = useParams();
  const navigate = useNavigate();
  const addToast = useToastContext();
  const { user } = useAuth();

  // Pack & Texturen
  const [pack, setPack] = useState(null);
  const [packFiles, setPackFiles] = useState([]);
  const [vanillaFiles, setVanillaFiles] = useState([]);
  const [translations, setTranslations] = useState({}); // assetPath -> deutscher Name
  const [loading, setLoading] = useState(true);
  const [vanillaLoading, setVanillaLoading] = useState(false);

  // Navigation
  const [activeCategory, setActiveCategory] = useState('block');
  const [search, setSearch] = useState('');
  const [selectedTexture, setSelectedTexture] = useState(null);
  const [geometryType, setGeometryType] = useState('block'); // 'block' | 'item' | 'cross' | null

  // Editor — viewMode nur noch fuer Entity/GUI/Misc (kein 3D-Modell)
  // Bei block/item/cross: immer beide Ansichten nebeneinander
  const [viewMode, setViewMode] = useState('both'); // 'both' | '2d'
  const [tool, setTool] = useState('pencil');
  const [color, setColor] = useState([0, 0, 0, 255]);
  const [zoom, setZoom] = useState(16);
  const [showGrid, setShowGrid] = useState(true);
  const [rectFilled, setRectFilled] = useState(false);
  const [symmetryMode, setSymmetryMode] = useState('none');
  const [recentColors, setRecentColors] = useState([]);
  const [canvasSize, setCanvasSize] = useState(16);
  const [canvasHeight, setCanvasHeight] = useState(16); // separate height for animation strips
  const [currentFrame, setCurrentFrame] = useState(0); // animation frame index
  const [totalFrames, setTotalFrames] = useState(1); // total frames in animation strip
  const [isAnimated, setIsAnimated] = useState(false); // is this an animated texture?
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  // Feature: Textur in anderes Pack kopieren
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [allPacks, setAllPacks] = useState([]);
  const [copyTargetPackId, setCopyTargetPackId] = useState('');
  const [copyTargetFiles, setCopyTargetFiles] = useState([]);
  const [copyTargetPreviewUrl, setCopyTargetPreviewUrl] = useState(null);
  const [copying, setCopying] = useState(false);

  // Feature: ZIP-Import Vorschau
  const [showZipImportModal, setShowZipImportModal] = useState(false);
  const [zipImportEntries, setZipImportEntries] = useState([]); // [{assetPath, dataUrl, selected}]
  const [zipImportPending, setZipImportPending] = useState(false);
  const [zipImportConfirming, setZipImportConfirming] = useState(false);
  const [zipImportSearch, setZipImportSearch] = useState('');

  // Feature: ZIP-Download Name anpassen
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadFileName, setDownloadFileName] = useState('');

  // 3D Painter Textur-URL (aktuelle Textur als DataURL) — Single-Textur Mode
  const [textureDataUrl, setTextureDataUrl] = useState(null);
  // Multi-Textur Mode: { right, left, top, bottom, front, back } als dataUrls
  const [faceTextures, setFaceTextures] = useState(null);
  // Gruppen-Info wenn Multi-Face-Block ausgewaehlt
  const [selectedGroup, setSelectedGroup] = useState(null); // { groupKey, label, faces }
  // Textur-Slots fuer 2D-Ansicht (einzigartige Texturen der Gruppe)
  const [textureSlots, setTextureSlots] = useState([]); // [{label, assetPath, faceKeys}]
  // Refs fuer mehrere 2D-Canvases (eine pro Slot)
  const slotCanvasRefs = useRef({});
  const [showTilePreview, setShowTilePreview] = useState(false);

  // Lokaler Cache fuer ungespeicherte Aenderungen (assetPath -> dataUrl / multi: { faceKey -> dataUrl })
  const unsavedEditsRef = useRef({});

  const canvasRef   = useRef(null);
  const painter3dRef = useRef(null);
  const tilePreviewRef = useRef(null);
  const history = usePixelHistory();

  useEffect(() => {
    loadPackData();
  }, [packId]);

  // Tile-Vorschau aktualisieren
  useEffect(() => {
    if (!showTilePreview || !tilePreviewRef.current || !canvasRef.current) return;
    const updateTile = () => {
      const dataURL = canvasRef.current?.getDataURL();
      if (!dataURL || !tilePreviewRef.current) return;
      const ctx = tilePreviewRef.current.getContext('2d');
      const size = canvasSize;
      const previewSize = 128;
      const tilesPerRow = Math.floor(previewSize / size);
      tilePreviewRef.current.width = previewSize;
      tilePreviewRef.current.height = previewSize;
      ctx.clearRect(0, 0, previewSize, previewSize);

      const img = new Image();
      img.onload = () => {
        ctx.imageSmoothingEnabled = false;
        for (let row = 0; row < tilesPerRow; row++) {
          for (let col = 0; col < tilesPerRow; col++) {
            ctx.drawImage(img, col * size, row * size, size, size);
          }
        }
      };
      img.src = dataURL;
    };
    const timer = setInterval(updateTile, 500);
    updateTile();
    return () => clearInterval(timer);
  }, [showTilePreview, canvasSize]);

  async function loadPackData() {
    setLoading(true);
    try {
      const packData = await api.getTexturePack(packId);
      setPack(packData);
      setPackFiles(packData.files || []);

      // Vanilla-Texturen laden falls gecacht
      try {
        const vanillaData = await api.getVanillaAssets(packData.mcVersion);
        setVanillaFiles(vanillaData.files || []);
        // Deutsche Textur-Namen laden
        try {
          const trans = await api.getVanillaTranslations(packData.mcVersion);
          setTranslations(trans || {});
        } catch {
          // Sprachdatei noch nicht verfuegbar
        }
      } catch {
        // Noch nicht gecacht - kein Problem
      }
    } catch (err) {
      addToast('Fehler beim Laden: ' + err.message, 'error');
      navigate('/texture-packs');
    } finally {
      setLoading(false);
    }
  }

  async function fetchVanillaTextures() {
    if (!pack) return;
    setVanillaLoading(true);
    try {
      const result = await api.fetchVanillaAssets(pack.mcVersion);
      addToast(result.message || 'Vanilla-Texturen geladen!', 'success');
      const vanillaData = await api.getVanillaAssets(pack.mcVersion);
      setVanillaFiles(vanillaData.files || []);
      // Translations ebenfalls (neu) laden
      try {
        const trans = await api.getVanillaTranslations(pack.mcVersion);
        setTranslations(trans || {});
      } catch {
        // Sprachdatei noch nicht verfuegbar (wird im Hintergrund nachgeladen)
      }
    } catch (err) {
      addToast('Fehler beim Laden der Vanilla-Texturen: ' + err.message, 'error');
    } finally {
      setVanillaLoading(false);
    }
  }

  // Hilfsfunktion: Textur mit Auth als DataURL laden
  async function fetchAsDataUrl(imgUrl) {
    const token = getAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await fetch(imgUrl, { headers });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const bmp = await createImageBitmap(blob);
    const w = bmp.width, h = bmp.height;
    const offscreen = document.createElement('canvas');
    offscreen.width = w; offscreen.height = h;
    const ctx = offscreen.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    return { dataUrl: offscreen.toDataURL('image/png'), imageData: ctx.getImageData(0, 0, w, h), width: w, height: h };
  }

  // Aktuellen Canvas-Inhalt in den lokalen Cache sichern bevor eine andere Textur geladen wird
  function saveCurrentToCache() {
    if (!selectedTexture) return;
    const path = selectedTexture.assetPath;
    if (selectedGroup && painter3dRef.current) {
      // Multi-Face: alle Face-DataUrls sichern
      const allUrls = painter3dRef.current.getAllFaceDataUrls();
      if (allUrls && Object.keys(allUrls).length > 0) {
        unsavedEditsRef.current[path] = { multi: true, faces: allUrls };
      }
    } else if (painter3dRef.current) {
      const dataUrl = painter3dRef.current.getDataUrl();
      if (dataUrl) unsavedEditsRef.current[path] = { multi: false, dataUrl };
    } else if (canvasRef.current) {
      const dataUrl = canvasRef.current.getDataURL();
      if (dataUrl) unsavedEditsRef.current[path] = { multi: false, dataUrl };
    }
  }

  // Textur laden (von Pack oder Vanilla)
  async function loadTexture(assetPath) {
    // Alten Stand in Cache sichern
    saveCurrentToCache();

    setSelectedTexture({ assetPath });
    setFaceTextures(null);
    setSelectedGroup(null);
    setTextureSlots([]);
    setTextureDataUrl(null);
    history.reset();

    // Geometrie-Typ bestimmen
    const geoType = getGeometryType(assetPath);
    setGeometryType(geoType);
    if (!geoType) {
      setViewMode('2d');
    } else {
      setViewMode('both');
    }

    // URL fuer diese Textur
    function getUrl(path) {
      const packFile = packFiles.find(f => f.assetPath === path);
      if (packFile) return api.getTextureFileImageUrl(packId, packFile.id) + '?t=' + Date.now();
      return api.getVanillaTextureUrl(pack.mcVersion, path);
    }

    try {
      // Pruefen ob es lokale ungespeicherte Aenderungen gibt
      const cached = unsavedEditsRef.current[assetPath];

      // Multi-Face-Block?
      const group = getBlockGroup(assetPath);
      if (group && geoType === 'block') {
        // Alle einzigartigen Slots ermitteln
        const slots = getUniqueTextureSlots(group.faces);
        setTextureSlots(slots);
        setSelectedGroup(group);

        // Alle Face-Texturen laden
        const sixFaces = resolveToSixFaces(group.faces);
        const faceUrls = {};
        const slotDataUrls = {};

        if (cached && cached.multi) {
          // Aus Cache laden
          for (const [faceKey, path] of Object.entries(sixFaces)) {
            faceUrls[faceKey] = cached.faces[faceKey] || null;
          }
          for (const slot of slots) {
            // Finde einen faceKey der auf diesen Slot zeigt
            const fk = slot.faceKeys[0];
            slotDataUrls[slot.assetPath] = cached.faces[fk] || null;
          }
        } else {
          // Vom Server laden
          const uniquePaths = [...new Set(Object.values(sixFaces))];
          const pathToDataUrl = {};
          for (const p of uniquePaths) {
            try {
              const { dataUrl } = await fetchAsDataUrl(getUrl(p));
              pathToDataUrl[p] = dataUrl;
            } catch {
              pathToDataUrl[p] = null;
            }
          }
          for (const [faceKey, path] of Object.entries(sixFaces)) {
            faceUrls[faceKey] = pathToDataUrl[path] || null;
          }
          for (const slot of slots) {
            slotDataUrls[slot.assetPath] = pathToDataUrl[slot.assetPath] || null;
          }
        }

        setFaceTextures(faceUrls);

        // 2D-Canvases befuellen
        setTimeout(() => {
          for (const slot of slots) {
            const ref = slotCanvasRefs.current[slot.assetPath];
            if (ref && slotDataUrls[slot.assetPath]) {
              ref.loadFromDataURL(slotDataUrls[slot.assetPath]);
            }
          }
          // History mit dem ersten Slot initialisieren
          if (slots.length > 0) {
            const firstRef = slotCanvasRefs.current[slots[0].assetPath];
            if (firstRef) {
              const imgData = firstRef.getImageData();
              if (imgData) history.push(imgData);
            }
          }
        }, 100);

      } else {
        // Single-Textur
        let dataUrl, imageData, size, imgHeight;
        if (cached && !cached.multi && cached.dataUrl) {
          // Aus Cache laden
          dataUrl = cached.dataUrl;
          // DataURL → ImageData konvertieren
          const img = await new Promise((resolve, reject) => {
            const i = new Image(); i.onload = () => resolve(i); i.onerror = reject; i.src = dataUrl;
          });
          size = img.width;
          imgHeight = img.height;
          const offscreen = document.createElement('canvas');
          offscreen.width = size; offscreen.height = imgHeight;
          const ctx = offscreen.getContext('2d');
          ctx.drawImage(img, 0, 0);
          imageData = ctx.getImageData(0, 0, size, imgHeight);
        } else {
          // Vom Server laden
          const result = await fetchAsDataUrl(getUrl(assetPath));
          dataUrl = result.dataUrl;
          imageData = result.imageData;
          size = result.width;
          imgHeight = result.height;
        }

        // Detect animated texture (height > width = animation strip)
        const animated = imgHeight > size;
        const frames = animated ? Math.floor(imgHeight / size) : 1;
        setIsAnimated(animated);
        setTotalFrames(frames);
        setCurrentFrame(0);
        setCanvasSize(size);
        setCanvasHeight(animated ? imgHeight : size);
        const newZoom = Math.max(2, Math.min(32, Math.floor(400 / size)));
        setZoom(newZoom);
        setTextureDataUrl(dataUrl);

        if (canvasRef.current) {
          setTimeout(() => {
            if (canvasRef.current) {
              canvasRef.current.setImageData(imageData);
              history.push(imageData);
            }
          }, 50);
        }
      }
    } catch (err) {
      addToast('Fehler beim Laden der Textur: ' + err.message, 'error');
    }
  }

  // 3D → 2D sync (Live, kein History-Push)
  function handlePaint3D(imgData, faceKey) {
    if (faceKey && selectedGroup) {
      // Multi-Face: richtigen Slot-Canvas updaten
      const slot = textureSlots.find(s => s.faceKeys.includes(faceKey));
      if (slot && slotCanvasRefs.current[slot.assetPath]) {
        slotCanvasRefs.current[slot.assetPath].setImageData(imgData);
      }
    } else if (canvasRef.current) {
      canvasRef.current.setImageData(imgData);
    }
  }

  // 2D → 3D sync (Live, kein History-Push)
  function handlePaint2D(imgData, assetPath) {
    if (selectedGroup && painter3dRef.current) {
      // Welche faceKeys gehören zu diesem assetPath?
      const slot = textureSlots.find(s => s.assetPath === assetPath);
      if (slot) {
        slot.faceKeys.forEach(fk => painter3dRef.current.setFaceImageData(fk, imgData));
      }
    } else if (painter3dRef.current) {
      painter3dRef.current.setImageData(imgData);
    }
  }

  // History-Push nur bei Strich-Ende
  function handleStrokeEnd(imgData, faceKey) {
    history.push(imgData);
  }

  // Undo/Redo: immer beide Views synchronisieren
  function handleUndo() {
    const prev = history.undo();
    if (!prev) return;
    if (canvasRef.current) canvasRef.current.setImageData(prev);
    if (painter3dRef.current) painter3dRef.current.setImageData(prev);
  }

  function handleRedo() {
    const next = history.redo();
    if (!next) return;
    if (canvasRef.current) canvasRef.current.setImageData(next);
    if (painter3dRef.current) painter3dRef.current.setImageData(next);
  }

  async function handleSaveTexture() {
    if (!selectedTexture) return;
    setSaving(true);
    try {
      if (selectedGroup && painter3dRef.current) {
        // Multi-Face: alle einzigartigen Slots speichern
        const allUrls = painter3dRef.current.getAllFaceDataUrls();
        // Einzigartige assetPaths bestimmen
        const sixFaces = resolveToSixFaces(selectedGroup.faces);
        const saved = new Set();
        for (const [faceKey, assetPath] of Object.entries(sixFaces)) {
          if (saved.has(assetPath)) continue;
          saved.add(assetPath);
          const dataURL = allUrls[faceKey];
          if (dataURL) {
            await api.saveTextureFile(packId, assetPath, dataURL);
          }
        }
      } else {
        // Single-Textur
        let dataURL = null;
        if (isAnimated && canvasRef.current) {
          // Animated texture: always use 2D canvas (has full strip height)
          dataURL = canvasRef.current.getDataURL();
        } else if (viewMode === 'both' && painter3dRef.current) {
          dataURL = painter3dRef.current.getDataUrl();
        } else if (canvasRef.current) {
          dataURL = canvasRef.current.getDataURL();
        }
        if (!dataURL) throw new Error('Kein Canvas-Inhalt');
        await api.saveTextureFile(packId, selectedTexture.assetPath, dataURL);
      }
      const packData = await api.getTexturePack(packId);
      setPackFiles(packData.files || []);
      // Cache fuer diese Textur loeschen (Server hat den aktuellen Stand)
      if (selectedTexture) delete unsavedEditsRef.current[selectedTexture.assetPath];
      addToast('Textur gespeichert!', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteTexture(assetPath) {
    const packFile = packFiles.find(f => f.assetPath === assetPath);
    if (!packFile) return;
    try {
      await api.deleteTextureFile(packId, packFile.id);
      setPackFiles(prev => prev.filter(f => f.id !== packFile.id));
      delete unsavedEditsRef.current[assetPath];
      if (selectedTexture?.assetPath === assetPath) {
        setSelectedTexture(null);
        if (canvasRef.current) canvasRef.current.clear();
      }
      addToast('Textur entfernt (Vanilla-Standard wird verwendet)', 'success');
    } catch (err) {
      addToast('Fehler: ' + err.message, 'error');
    }
  }

  async function handleLoadVanillaTemplate() {
    if (!selectedTexture || !pack) return;
    try {
      if (selectedGroup) {
        // Multi-Face: alle Slots neu laden
        const sixFaces = resolveToSixFaces(selectedGroup.faces);
        const uniquePaths = [...new Set(Object.values(sixFaces))];
        const pathToDataUrl = {};
        for (const p of uniquePaths) {
          const url = api.getVanillaTextureUrl(pack.mcVersion, p);
          const { dataUrl } = await fetchAsDataUrl(url);
          pathToDataUrl[p] = dataUrl;
        }
        const faceUrls = {};
        for (const [fk, p] of Object.entries(sixFaces)) faceUrls[fk] = pathToDataUrl[p];
        setFaceTextures({ ...faceUrls }); // triggert 3D-Rebuild
        // 2D-Canvases auch updaten
        for (const slot of textureSlots) {
          const ref = slotCanvasRefs.current[slot.assetPath];
          if (ref && pathToDataUrl[slot.assetPath]) ref.loadFromDataURL(pathToDataUrl[slot.assetPath]);
        }
      } else {
        const url = api.getVanillaTextureUrl(pack.mcVersion, selectedTexture.assetPath);
        const { dataUrl, imageData } = await fetchAsDataUrl(url);
        if (canvasRef.current) { canvasRef.current.setImageData(imageData); history.push(imageData); }
        if (painter3dRef.current) painter3dRef.current.loadFromDataUrl(dataUrl);
        setTextureDataUrl(dataUrl);
      }
      // Cache loeschen (Vanilla-Original geladen)
      if (selectedTexture) delete unsavedEditsRef.current[selectedTexture.assetPath];
    } catch (err) {
      addToast('Vanilla-Textur konnte nicht geladen werden: ' + err.message, 'error');
    }
  }

  function handlePixelChange(newData, assetPath) {
    handlePaint2D(newData, assetPath);
  }

  function handleAddRecentColor(c) {
    setRecentColors(prev => {
      const filtered = prev.filter(rc => !(rc[0] === c[0] && rc[1] === c[1] && rc[2] === c[2] && rc[3] === c[3]));
      return [c, ...filtered].slice(0, 16);
    });
  }

  // Feature: Textur in anderes Pack kopieren — Modal öffnen
  async function handleOpenCopyModal() {
    if (!selectedTexture) return;
    try {
      const data = await api.getTexturePacks();
      const others = (data.own || []).filter(p => p.id !== packId);
      setAllPacks(others);
      setCopyTargetPackId(others.length > 0 ? others[0].id : '');
      setCopyTargetFiles([]);
      setCopyTargetPreviewUrl(null);
      setShowCopyModal(true);
      if (others.length > 0) {
        loadCopyTargetPreview(others[0].id, others);
      }
    } catch (err) {
      addToast('Packs konnten nicht geladen werden: ' + err.message, 'error');
    }
  }

  async function loadCopyTargetPreview(targetId, packs) {
    const target = (packs || allPacks).find(p => p.id === targetId);
    if (!target || !selectedTexture) return;
    setCopyTargetPreviewUrl(null);
    setCopyTargetFiles([]);
    try {
      const files = await api.getTexturePackFiles(targetId);
      setCopyTargetFiles(files || []);
      const existing = (files || []).find(f => f.assetPath === selectedTexture.assetPath);
      if (existing) {
        const imgUrl = api.getTextureFileImageUrl(targetId, existing.id) + '?t=' + Date.now();
        const { dataUrl } = await fetchAsDataUrl(imgUrl);
        setCopyTargetPreviewUrl(dataUrl);
      }
    } catch { /* kein Fehler anzeigen */ }
  }

  async function handleCopyTexture() {
    if (!selectedTexture || !copyTargetPackId) return;
    setCopying(true);
    try {
      let dataURL = null;
      if (viewMode === 'both' && painter3dRef.current) {
        dataURL = painter3dRef.current.getDataUrl();
      } else if (canvasRef.current) {
        dataURL = canvasRef.current.getDataURL();
      }
      if (!dataURL) throw new Error('Kein Canvas-Inhalt');
      await api.saveTextureFile(copyTargetPackId, selectedTexture.assetPath, dataURL);
      addToast('Textur erfolgreich kopiert!', 'success');
      setShowCopyModal(false);
    } catch (err) {
      addToast('Fehler beim Kopieren: ' + err.message, 'error');
    } finally {
      setCopying(false);
    }
  }

  // Feature: ZIP-Import Vorschau — ZIP clientseitig parsen mit JSZip
  async function handleImportZipPreview(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setZipImportPending(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(file);
      const entries = [];
      const promises = [];
      zip.forEach((relativePath, zipEntry) => {
        if (zipEntry.dir) return;
        if (!relativePath.startsWith('assets/minecraft/textures/')) return;
        if (!relativePath.endsWith('.png')) return;
        const assetPath = relativePath.replace('assets/minecraft/textures/', '');
        promises.push(
          zipEntry.async('base64').then(b64 => {
            entries.push({
              assetPath,
              dataUrl: `data:image/png;base64,${b64}`,
              b64,
              selected: true,
            });
          })
        );
      });
      await Promise.all(promises);
      entries.sort((a, b) => a.assetPath.localeCompare(b.assetPath));
      setZipImportEntries(entries);
      setShowZipImportModal(true);
    } catch (err) {
      addToast('ZIP konnte nicht gelesen werden: ' + err.message, 'error');
    } finally {
      setZipImportPending(false);
    }
  }

  async function handleZipImportConfirm() {
    const selected = zipImportEntries.filter(e => e.selected);
    if (selected.length === 0) return;
    setZipImportConfirming(true);
    try {
      const textures = selected.map(e => ({ assetPath: e.assetPath, textureDataBase64: e.b64 }));
      const result = await api.importTexturePackSelected(packId, textures);
      addToast(`${result.importedCount} Texturen importiert!`, 'success');
      const packData = await api.getTexturePack(packId);
      setPackFiles(packData.files || []);
      setShowZipImportModal(false);
      setZipImportEntries([]);
    } catch (err) {
      addToast('Import-Fehler: ' + err.message, 'error');
    } finally {
      setZipImportConfirming(false);
    }
  }

  // Feature: ZIP-Download mit Namensanpassung
  function handleOpenDownloadModal() {
    setDownloadFileName(pack?.name || 'texture_pack');
    setShowDownloadModal(true);
  }

  function handleDownloadWithName() {
    const url = api.downloadTexturePackUrl(packId);
    const token = getAuthToken();
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        const safe = (downloadFileName || pack?.name || 'texture_pack').replace(/[^a-zA-Z0-9_\-. ]/g, '_');
        a.download = safe.endsWith('.zip') ? safe : `${safe}.zip`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(err => addToast('Download-Fehler: ' + err.message, 'error'));
    setShowDownloadModal(false);
  }

  // Textur-Dateien importieren (ZIP hochladen) — LEGACY, nicht mehr direkt verwendet
  async function handleImportZip(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const buffer = await file.arrayBuffer();
      // Chunked Base64-Konvertierung — verhindert "Maximum call stack size exceeded"
      // bei grossen ZIP-Dateien (Spread-Operator mit Millionen Argumenten crasht den Stack)
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const chunkSize = 8192;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
      }
      const base64 = btoa(binary);
      const result = await api.importTexturePack(packId, base64);
      addToast(`${result.importedCount} Texturen importiert!`, 'success');
      const packData = await api.getTexturePack(packId);
      setPackFiles(packData.files || []);
    } catch (err) {
      addToast('Import-Fehler: ' + err.message, 'error');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  }

  // Textur-Liste filtern
  const getCategoryTextures = useCallback(() => {
    const cat = TEXTURE_CATEGORIES.find(c => c.id === activeCategory);
    if (!cat) return [];

    const allPaths = new Set([
      ...packFiles.map(f => f.assetPath),
      ...vanillaFiles.filter(f => cat.path ? f.startsWith(cat.path) : !TEXTURE_CATEGORIES.some(c => c.path && c.id !== 'misc' && f.startsWith(c.path))),
    ]);

    let paths = Array.from(allPaths).filter(p => {
      // Filter out .mcmeta companion files — they are not editable textures
      if (p.endsWith('.mcmeta')) return false;
      if (cat.id === 'misc') return !TEXTURE_CATEGORIES.filter(c => c.path).some(c => p.startsWith(c.path));
      return p.startsWith(cat.path);
    });

    if (search) {
      const q = search.toLowerCase();
      paths = paths.filter(p => {
        if (p.toLowerCase().includes(q)) return true;
        const germanName = translations[p];
        if (germanName && germanName.toLowerCase().includes(q)) return true;
        return false;
      });
    }

    // Gruppen zusammenfassen: nur den "Haupt-Eintrag" einer Gruppe anzeigen
    const seenGroups = new Set();
    const result = [];
    for (const p of paths.sort()) {
      const group = getBlockGroup(p);
      if (group) {
        if (seenGroups.has(group.groupKey)) continue; // bereits hinzugefuegt
        seenGroups.add(group.groupKey);
        // Haupttextur der Gruppe (sides oder erster Wert)
        const mainPath = group.faces.sides || group.faces.all || Object.values(group.faces)[0];
        const uniqueCount = new Set(Object.values(group.faces)).size;
        result.push({
          assetPath: mainPath,
          displayPath: p, // der angeklickte Pfad
          isModified: Object.values(group.faces).some(fp => packFiles.some(f => f.assetPath === fp)),
          fileName: group.label || mainPath.split('/').pop(),
          germanName: group.label,
          isGroup: true,
          groupTextureCount: uniqueCount,
          groupKey: group.groupKey,
        });
      } else {
        result.push({
          assetPath: p,
          displayPath: p,
          isModified: packFiles.some(f => f.assetPath === p),
          fileName: p.split('/').pop(),
          germanName: translations[p] || null,
          isGroup: false,
        });
      }
    }
    return result;
  }, [activeCategory, packFiles, vanillaFiles, search, translations]);

  const categoryTextures = getCategoryTextures();

  if (loading) {
    return <div className="page-content"><div className="empty-state"><div className="spinner" /></div></div>;
  }

  return (
    <div className="texture-editor-layout">
      {/* Header */}
      <div className="texture-editor-header">
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/texture-packs')}>
          ← Bibliothek
        </button>
        <div className="texture-editor-title">
          <h2>{pack?.name}</h2>
          <div className="flex gap-2">
            <span className="tag">MC {pack?.mcVersion}</span>
            <span className="tag">{packFiles.length} Texturen geändert</span>
          </div>
        </div>
        <div className="flex gap-2">
          <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
            {zipImportPending ? 'Liest ZIP...' : '⬆ ZIP Import'}
            <input type="file" accept=".zip" style={{ display: 'none' }} onChange={handleImportZipPreview} disabled={zipImportPending} />
          </label>
          <button className="btn btn-secondary btn-sm" onClick={handleOpenDownloadModal}>
            ⬇ ZIP Download
          </button>
        </div>
      </div>

      <div className="texture-editor-body">
        {/* Linke Seite: Textur-Browser */}
        <div className="texture-editor-sidebar-left">
          {/* Kategorie-Tabs */}
          <div className="texture-category-tabs">
            {TEXTURE_CATEGORIES.map(cat => (
              <button
                key={cat.id}
                className={`texture-cat-btn ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
                title={cat.label}
              >
                {cat.icon}
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Suche */}
          <div style={{ padding: '8px 12px' }}>
            <input
              type="text"
              className="mc-input"
              placeholder="Textur suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>

          {/* Vanilla-Texturen Hinweis */}
           {vanillaFiles.length === 0 && (
             <div style={{ padding: '8px 12px' }}>
               <div className="info-box" style={{ fontSize: 12, padding: '8px 10px' }}>
                 <p>Keine Vanilla-Texturen geladen.</p>
                 <button
                   className="btn btn-primary btn-sm"
                   style={{ marginTop: 6, width: '100%' }}
                   onClick={fetchVanillaTextures}
                   disabled={vanillaLoading}
                 >
                   {vanillaLoading ? 'Lädt...' : `MC ${pack?.mcVersion} Texturen laden`}
                 </button>
               </div>
             </div>
           )}

          {/* Textur-Liste */}
          <div className="texture-file-list">
            {categoryTextures.length === 0 ? (
              <div className="text-sm text-muted" style={{ padding: 12 }}>
                {search ? 'Keine Texturen gefunden' : 'Keine Texturen in dieser Kategorie'}
              </div>
            ) : (
              categoryTextures.map(tex => (
                <div
                  key={tex.assetPath}
                  className={`texture-file-item ${selectedTexture?.assetPath === tex.assetPath ? 'active' : ''} ${tex.isModified ? 'modified' : ''}`}
                  onClick={() => loadTexture(tex.displayPath || tex.assetPath)}
                >
                  <div className="texture-file-preview">
                    <TextureThumbnail
                      packId={packId}
                      packFiles={packFiles}
                      assetPath={tex.assetPath}
                      mcVersion={pack?.mcVersion}
                      isModified={tex.isModified}
                    />
                  </div>
                  <div className="texture-file-name">
                    {tex.germanName ? (
                      <>
                        <span style={{ fontWeight: 500 }}>{tex.germanName}</span>
                        {!tex.isGroup && <span className="text-muted" style={{ fontSize: 10, display: 'block', lineHeight: 1.2 }}>{tex.fileName}</span>}
                        {tex.isGroup && <span className="text-muted" style={{ fontSize: 10, display: 'block', lineHeight: 1.2 }}>{tex.groupTextureCount} Texturen</span>}
                      </>
                    ) : (
                      <>
                        <span>{tex.fileName}</span>
                        {tex.isGroup && <span className="text-muted" style={{ fontSize: 10, display: 'block' }}>{tex.groupTextureCount} Texturen</span>}
                      </>
                    )}
                    {tex.isModified && <span className="texture-modified-badge">●</span>}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Mitte: Editor */}
        <div className="texture-editor-canvas-area">
          {selectedTexture ? (
            <>
              {/* Textur-Header */}
              <div className="texture-edit-header">
                <div>
                  <strong>
                    {selectedGroup
                      ? selectedGroup.label
                      : (translations[selectedTexture.assetPath] || selectedTexture.assetPath.split('/').pop())}
                  </strong>
                  <span className="text-muted" style={{ fontSize: 12, marginLeft: 8 }}>
                    {selectedGroup
                      ? `${new Set(Object.values(selectedGroup.faces)).size} Texturen`
                      : selectedTexture.assetPath}
                  </span>
                </div>
                <div className="flex gap-2" style={{ alignItems: 'center' }}>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleLoadVanillaTemplate}
                    title="Vanilla-Standard-Textur als Vorlage laden"
                  >
                    Vanilla laden
                  </button>
                  {packFiles.some(f => f.assetPath === selectedTexture.assetPath) && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteTexture(selectedTexture.assetPath)}
                    >
                      Zuruecksetzen
                    </button>
                  )}
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={handleOpenCopyModal}
                    title="Diese Textur in ein anderes Pack kopieren"
                  >
                    → In Pack kopieren
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveTexture}
                    disabled={saving}
                  >
                    {saving ? 'Speichert...' : '💾 Speichern'}
                  </button>
                </div>
              </div>

              {/* Toolbar */}
              <ToolBar
                activeTool={tool}
                onToolChange={setTool}
                zoom={zoom}
                onZoomChange={setZoom}
                showGrid={showGrid}
                onToggleGrid={() => setShowGrid(v => !v)}
                rectFilled={rectFilled}
                onToggleRectFilled={setRectFilled}
                symmetryMode={symmetryMode}
                onSymmetryChange={setSymmetryMode}
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={history.canUndo}
                canRedo={history.canRedo}
                onClear={() => { canvasRef.current?.clear(); if (painter3dRef.current) painter3dRef.current.clear(); }}
                onFill={() => {
                  const [r, g, b, a] = color;
                  canvasRef.current?.fill(r, g, b, a);
                }}
              />

              {/* Haupt-Editor-Bereich: 3D links + 2D rechts (nebeneinander) */}
              <div style={{ flex: 1, display: 'flex', minHeight: 0, gap: 0 }}>

                {/* 3D Painter (nur wenn geometryType gesetzt) */}
                {viewMode === 'both' && geometryType && (
                  <div style={{
                    flex: '0 0 50%', display: 'flex', flexDirection: 'column',
                    borderRight: '1px solid var(--border)',
                  }}>
                    <div style={{
                      display: 'flex', gap: 6, padding: '4px 10px',
                      background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)',
                      alignItems: 'center', flexShrink: 0,
                    }}>
                      <span className="text-sm text-muted">
                        {geometryType === 'item' ? '3D Item' : geometryType === 'cross' ? '3D Pflanze' : '3D Block'}
                      </span>
                      <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                        Rechtsklick + Ziehen = Drehen | Scroll = Zoom
                      </span>
                    </div>
                    <div style={{ flex: 1, minHeight: 0 }}>
                      {(textureDataUrl || faceTextures) && (
                        <Texture3DPainter
                          ref={painter3dRef}
                          textureDataUrl={faceTextures ? null : textureDataUrl}
                          faceTextures={faceTextures || null}
                          geometryType={geometryType || 'block'}
                          tool={tool}
                          color={color}
                          brushSize={1}
                          canvasSize={canvasSize}
                          isAnimated={isAnimated}
                          animFrameCount={totalFrames}
                          onPaint={handlePaint3D}
                          onStrokeEnd={handleStrokeEnd}
                          onColorPick={(c) => { setColor(c); setTool('pencil'); }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* 2D Pixel Canvas */}
                <div style={{
                  flex: viewMode === 'both' && geometryType ? '0 0 50%' : '1',
                  display: 'flex', flexDirection: 'column', overflow: 'auto',
                  background: 'var(--bg-primary)',
                }}>
                  {viewMode === 'both' && geometryType && (
                    <div style={{
                      padding: '4px 10px', background: 'var(--bg-secondary)',
                      borderBottom: '1px solid var(--border)', flexShrink: 0,
                    }}>
                      <span className="text-sm text-muted">2D Pixel-Editor</span>
                    </div>
                  )}
                  <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
                    {selectedGroup && textureSlots.length > 0 ? (
                      /* Multi-Face: alle Slots untereinander */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {textureSlots.map(slot => (
                          <div key={slot.assetPath}>
                            <div className="text-sm" style={{ marginBottom: 4, fontWeight: 600 }}>
                              {slot.label}
                              <span className="text-muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: 11 }}>
                                {slot.assetPath.split('/').pop()}
                              </span>
                            </div>
                            <PixelCanvas
                              ref={el => { slotCanvasRefs.current[slot.assetPath] = el; }}
                              width={canvasSize}
                              height={canvasSize}
                              zoom={zoom}
                              tool={tool}
                              color={color}
                              showGrid={showGrid}
                              onPixelChange={(d) => handlePixelChange(d, slot.assetPath)}
                              onStrokeEnd={handleStrokeEnd}
                              onColorPick={c => setColor(c)}
                              rectFilled={rectFilled}
                              symmetryMode={symmetryMode}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Single-Textur */
                      <>
                        {isAnimated && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                            background: 'var(--bg-secondary)', borderRadius: 6, marginBottom: 8,
                            border: '1px solid var(--border)',
                          }}>
                            <span className="text-sm" style={{ fontWeight: 600 }}>Animation</span>
                            <button className="btn btn-secondary btn-sm" disabled={currentFrame === 0}
                              onClick={() => setCurrentFrame(f => Math.max(0, f - 1))}>◀</button>
                            <span className="text-sm">
                              Frame {currentFrame + 1} / {totalFrames}
                            </span>
                            <button className="btn btn-secondary btn-sm" disabled={currentFrame >= totalFrames - 1}
                              onClick={() => setCurrentFrame(f => Math.min(totalFrames - 1, f + 1))}>▶</button>
                            <span className="text-sm text-muted" style={{ marginLeft: 'auto' }}>
                              Strip: {canvasSize}×{canvasHeight}px ({totalFrames} Frames)
                            </span>
                          </div>
                        )}
                        <PixelCanvas
                          ref={canvasRef}
                          width={canvasSize}
                          height={canvasHeight}
                          zoom={zoom}
                          tool={tool}
                          color={color}
                          showGrid={showGrid}
                          onPixelChange={handlePixelChange}
                          onStrokeEnd={handleStrokeEnd}
                          onColorPick={c => setColor(c)}
                          rectFilled={rectFilled}
                          symmetryMode={symmetryMode}
                          scrollToY={isAnimated ? currentFrame * canvasSize : undefined}
                        />
                      </>
                    )}
                  </div>
                </div>

              </div>

            </>
          ) : (
            <div className="empty-state" style={{ flex: 1 }}>
              <div className="icon">🎨</div>
              <p>Waehle eine Textur aus der Liste zum Bearbeiten</p>
              <p className="text-sm text-muted">
                Klicke auf eine Textur links um sie in den Editor zu laden
              </p>
            </div>
          )}
        </div>

        {/* Rechte Seite: Farbe */}
        <div className="texture-editor-sidebar-right">
          <div className="skin-editor-panel">
            <div className="panel-title">Farbe</div>
            <ColorPicker
              color={color}
              onChange={setColor}
              recentColors={recentColors}
              onAddRecent={handleAddRecentColor}
            />
          </div>

          {/* Canvas-Groesse */}
          <div className="skin-editor-panel">
            <div className="panel-title">Canvas-Groesse</div>
            <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
              {[16, 32, 64].map(size => (
                <button
                  key={size}
                  className={`btn btn-sm ${canvasSize === size ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setCanvasSize(size)}
                >
                  {size}×{size}
                </button>
              ))}
            </div>
            <p className="text-sm text-muted" style={{ marginTop: 6 }}>
              Minecraft Standard: 16×16<br />
              Hohe Aufloesung: 32×32 oder 64×64
            </p>
          </div>

          {/* Kachel-Vorschau */}
          <div className="skin-editor-panel">
            <div className="panel-title">Kachel-Vorschau</div>
            <button
              className={`btn btn-sm ${showTilePreview ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setShowTilePreview(v => !v)}
              style={{ width: '100%', marginBottom: 8 }}
            >
              {showTilePreview ? 'Ausblenden' : 'Anzeigen'}
            </button>
            {showTilePreview && (
              <canvas
                ref={tilePreviewRef}
                style={{ width: 128, height: 128, imageRendering: 'pixelated', background: '#111', borderRadius: 4 }}
              />
            )}
          </div>

          {/* Pack-Info */}
          {pack && (
            <div className="skin-editor-panel">
              <div className="panel-title">Pack-Info</div>
              <div className="text-sm">
                <p><strong>Name:</strong> {pack.name}</p>
                <p><strong>Version:</strong> MC {pack.mcVersion}</p>
                <p><strong>pack_format:</strong> {pack.packFormat}</p>
                <p><strong>Texturen:</strong> {packFiles.length} geaendert</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal: Textur in anderes Pack kopieren */}
      {showCopyModal && (
        <div className="modal-overlay" onClick={() => setShowCopyModal(false)}>
          <div className="modal copy-texture-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Textur in anderes Pack kopieren</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCopyModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="copy-texture-previews">
                {/* Linke Seite: Aktuelle Textur */}
                <div className="copy-texture-side">
                  <div className="copy-texture-side-label">Aktuelle Textur</div>
                  <div className="copy-texture-preview-box">
                    {textureDataUrl ? (
                      <img src={textureDataUrl} alt="Aktuelle Textur" style={{ imageRendering: 'pixelated', maxWidth: 80, maxHeight: 80 }} />
                    ) : (
                      <span className="text-muted" style={{ fontSize: 12 }}>Keine Vorschau</span>
                    )}
                  </div>
                  <div className="copy-texture-path">{selectedTexture?.assetPath}</div>
                </div>

                <div className="copy-texture-arrow">→</div>

                {/* Rechte Seite: Ziel-Pack */}
                <div className="copy-texture-side">
                  <div className="copy-texture-side-label">Ziel-Pack</div>
                  <div className="copy-texture-preview-box">
                    {copyTargetPreviewUrl ? (
                      <img src={copyTargetPreviewUrl} alt="Ziel-Textur" style={{ imageRendering: 'pixelated', maxWidth: 80, maxHeight: 80 }} />
                    ) : (
                      <span className="text-muted" style={{ fontSize: 12 }}>Noch nicht vorhanden</span>
                    )}
                  </div>
                  <div className="copy-texture-path">
                    {copyTargetFiles.find(f => f.assetPath === selectedTexture?.assetPath)
                      ? <span style={{ color: 'var(--warning, #f59e0b)' }}>Wird überschrieben</span>
                      : <span style={{ color: 'var(--success, #22c55e)' }}>Neu</span>
                    }
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Ziel-Pack auswählen</label>
                {allPacks.length === 0 ? (
                  <p className="text-muted" style={{ fontSize: 13 }}>Keine anderen Packs vorhanden.</p>
                ) : (
                  <select
                    className="mc-select"
                    value={copyTargetPackId}
                    onChange={e => {
                      setCopyTargetPackId(e.target.value);
                      loadCopyTargetPreview(e.target.value, allPacks);
                    }}
                  >
                    {allPacks.map(p => (
                      <option key={p.id} value={p.id}>{p.name} (MC {p.mcVersion})</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCopyModal(false)}>Abbrechen</button>
              <button
                className="btn btn-primary"
                onClick={handleCopyTexture}
                disabled={copying || !copyTargetPackId || allPacks.length === 0}
              >
                {copying ? 'Kopiert...' : '→ Kopieren'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ZIP-Import Vorschau */}
      {showZipImportModal && (
        <div className="modal-overlay" onClick={() => { setShowZipImportModal(false); setZipImportEntries([]); setZipImportSearch(''); }}>
          <div className="modal zip-import-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>ZIP-Import — Texturen auswählen</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => { setShowZipImportModal(false); setZipImportEntries([]); setZipImportSearch(''); }}>✕</button>
            </div>
            <div className="modal-body" style={{ paddingBottom: 0 }}>
              <input
                type="text"
                className="mc-input"
                placeholder="Suchen nach Name oder Datei..."
                value={zipImportSearch}
                onChange={e => setZipImportSearch(e.target.value)}
                style={{ width: '100%' }}
              />
              <div className="zip-import-toolbar">
                <span className="text-sm text-muted">
                  {zipImportEntries.filter(e => e.selected).length} / {zipImportEntries.length} ausgewählt
                  {zipImportSearch && ` (${zipImportEntries.filter(e => {
                    const q = zipImportSearch.toLowerCase();
                    return (translations[e.assetPath] || '').toLowerCase().includes(q) || e.assetPath.toLowerCase().includes(q);
                  }).length} gefunden)`}
                </span>
                <div className="flex gap-2">
                  <button className="btn btn-secondary btn-sm" onClick={() => setZipImportEntries(prev => prev.map(e => ({ ...e, selected: true })))}>Alle</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setZipImportEntries(prev => prev.map(e => ({ ...e, selected: false })))}>Keine</button>
                </div>
              </div>
            </div>
            <div className="zip-import-list">
              {zipImportEntries.length === 0 ? (
                <div className="empty-state" style={{ padding: 24 }}>
                  <p className="text-muted">Keine Texturen in dieser ZIP gefunden.</p>
                </div>
              ) : (
                zipImportEntries
                  .filter(entry => {
                    if (!zipImportSearch) return true;
                    const q = zipImportSearch.toLowerCase();
                    return (
                      (translations[entry.assetPath] || '').toLowerCase().includes(q) ||
                      entry.assetPath.toLowerCase().includes(q)
                    );
                  })
                  .map(entry => {
                    return (
                      <label key={entry.assetPath} className={`zip-import-entry ${entry.selected ? 'selected' : ''}`}>
                        <input
                          type="checkbox"
                          checked={entry.selected}
                          onChange={e => setZipImportEntries(prev => prev.map(x => x.assetPath === entry.assetPath ? { ...x, selected: e.target.checked } : x))}
                        />
                        <img
                          src={entry.dataUrl}
                          alt={entry.assetPath}
                          className="zip-import-thumb"
                          style={{ imageRendering: 'pixelated' }}
                        />
                        <div className="zip-import-name">
                          <span>{translations[entry.assetPath] || entry.assetPath.split('/').pop()}</span>
                          <span className="text-muted" style={{ fontSize: 11 }}>{entry.assetPath}</span>
                        </div>
                        {packFiles.some(f => f.assetPath === entry.assetPath) && (
                          <span className="zip-import-overwrite-badge">überschreibt</span>
                        )}
                      </label>
                    );
                  })
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowZipImportModal(false); setZipImportEntries([]); setZipImportSearch(''); }}>Abbrechen</button>
              <button
                className="btn btn-primary"
                onClick={handleZipImportConfirm}
                disabled={zipImportConfirming || zipImportEntries.filter(e => e.selected).length === 0}
              >
                {zipImportConfirming ? 'Importiert...' : `${zipImportEntries.filter(e => e.selected).length} Texturen importieren`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: ZIP-Download Name anpassen */}
      {showDownloadModal && (
        <div className="modal-overlay" onClick={() => setShowDownloadModal(false)}>
          <div className="modal" style={{ minWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>ZIP herunterladen</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowDownloadModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Dateiname (ohne .zip)</label>
                <input
                  type="text"
                  className="mc-input"
                  value={downloadFileName}
                  onChange={e => setDownloadFileName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleDownloadWithName(); }}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDownloadModal(false)}>Abbrechen</button>
              <button className="btn btn-primary" onClick={handleDownloadWithName} disabled={!downloadFileName.trim()}>
                ⬇ Herunterladen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mini-Vorschau einer Textur in der Dateiliste
function TextureThumbnail({ packId, packFiles, assetPath, mcVersion, isModified }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    canvas.width = 24;
    canvas.height = 24;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, 24, 24);
    let cancelled = false;

    (async () => {
      try {
        const packFile = packFiles.find(f => f.assetPath === assetPath);
        let imgUrl;
        if (packFile) {
          imgUrl = api.getTextureFileImageUrl(packId, packFile.id) + '?t=' + Date.now();
        } else if (mcVersion) {
          imgUrl = api.getVanillaTextureUrl(mcVersion, assetPath);
        } else {
          return;
        }
        const token = getAuthToken();
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const resp = await fetch(imgUrl, { headers });
        if (!resp.ok || cancelled) return;
        const blob = await resp.blob();
        const bmp = await createImageBitmap(blob);
        if (cancelled) { bmp.close(); return; }
        ctx.imageSmoothingEnabled = false;
        // For animation strips (height > width), only draw the first frame
        if (bmp.height > bmp.width) {
          ctx.drawImage(bmp, 0, 0, bmp.width, bmp.width, 0, 0, 24, 24);
        } else {
          ctx.drawImage(bmp, 0, 0, 24, 24);
        }
        bmp.close();
      } catch {
        // Thumbnail konnte nicht geladen werden - leeres Feld
      }
    })();

    return () => { cancelled = true; };
  }, [packId, packFiles, assetPath, mcVersion, isModified]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: 24, height: 24, imageRendering: 'pixelated' }}
    />
  );
}

export default TextureEditor;
